import { DataRole, Ident } from '@mode-switch/express';
import { Field, Model } from '@mode-switch/model';
import { PivotDetail, PivotPlan, PivotQuery, PlanType, Query, SelectPlan } from '@mode-switch/query';
import { EncField, Encoding } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';

import { makeField } from '../field';
import { makeMarks } from '../mark';
import { makeSorts } from '../sort';
import { makeFilters } from '../filter';

export type ModeRole = FlamingoTypes.ModeRole;
export type FieldMap = Map<string, Field<ModeRole>>;

export function planPivotQuery(model: Model<ModeRole>, viewName: string, encoding: Encoding): PivotPlan<ModeRole> {
  const fields: FieldMap = new Map();
  const xFields: Readonly<EncField>[] = [...encoding.x, ...encoding.x2];
  const yFields: Readonly<EncField>[] = [...encoding.y, ...encoding.y2];

  const column: Field<ModeRole>[] = [];
  for (const encField of encoding.column) {
    column.push(makeField(model, viewName, encoding, fields, encField));
  }

  const row: Field<ModeRole>[] = [];
  for (const encField of encoding.row) {
    row.push(makeField(model, viewName, encoding, fields, encField));
  }

  const values: Field<ModeRole>[] = [];
  for (const encField of encoding.value) {
    values.push(makeField(model, viewName, encoding, fields, encField));
  }

  switch (encoding.marks.channel) {
    case 'x': {
      let plan = makeDetailPlan(model, viewName);

      const y: Field<ModeRole>[] = [];
      for (const yEncField of yFields) {
        y.push(makeField(model, viewName, encoding, fields, yEncField));
      }

      for (const encSeries of encoding.marks.series) {
        const seriesEncField = xFields.find((field) => field.id === encSeries.field);
        if (seriesEncField == null) {
          throw new Error(`Cannot find field in encoding referenced by series ${encSeries.field}`);
        }

        const seriesFields = new Map(fields);
        const seriesField = makeField(model, viewName, encoding, seriesFields, seriesEncField);
        const marks = makeMarks(model, viewName, encoding, seriesFields, encSeries.properties);
        const x: Field<ModeRole>[] = [seriesField];
        const series: PivotQuery.Series<ModeRole>[] = [
          {
            field: seriesField,
            marks,
          },
        ];

        const sorts = makeSorts(model, viewName, encoding, seriesFields, { addDefault: true });
        const filters = makeFilters(model, viewName, encoding, seriesFields);

        plan = addDetailPlan(
          plan,
          PivotQuery.Detail.make({
            column,
            x,
            row,
            y,
            values,
            series,
            sorts,
            filters,
          })
        );
      }

      return plan;
    }
    case 'y': {
      let plan = makeDetailPlan(model, viewName);

      const x: Field<ModeRole>[] = [];
      for (const xEncField of xFields) {
        x.push(makeField(model, viewName, encoding, fields, xEncField));
      }

      for (const encSeries of encoding.marks.series) {
        const seriesEncField = yFields.find((field) => field.id === encSeries.field);
        if (seriesEncField == null) {
          throw new Error(`Cannot find field in encoding referenced by series ${encSeries.field}`);
        }

        const seriesFields = new Map(fields);
        const seriesField = makeField(model, viewName, encoding, seriesFields, seriesEncField);
        const marks = makeMarks(model, viewName, encoding, seriesFields, encSeries.properties);
        const y: Field<ModeRole>[] = [seriesField];
        const series: PivotQuery.Series<ModeRole>[] = [
          {
            field: seriesField,
            marks,
          },
        ];

        const sorts = makeSorts(model, viewName, encoding, seriesFields, { addDefault: true });
        const filters = makeFilters(model, viewName, encoding, seriesFields);

        plan = addDetailPlan(
          plan,
          PivotQuery.Detail.make({
            column,
            x,
            row,
            y,
            values,
            series,
            sorts,
            filters,
          })
        );
      }

      return plan;
    }
    case '*': {
      const x: Field<ModeRole>[] = [];
      for (const xEncField of xFields) {
        x.push(makeField(model, viewName, encoding, fields, xEncField));
      }

      const y: Field<ModeRole>[] = [];
      for (const yEncField of yFields) {
        y.push(makeField(model, viewName, encoding, fields, yEncField));
      }

      const marks = makeMarks(model, viewName, encoding, fields, encoding.marks.properties);
      const sorts = makeSorts(model, viewName, encoding, fields, { addDefault: true });
      const filters = makeFilters(model, viewName, encoding, fields);

      return PivotPlan.make(
        model,
        viewName,
        PivotQuery.Basic.make({
          name: 'basic:*',
          column,
          x,
          row,
          y,
          values,
          marks,
          sorts,
          filters,
        })
      );
    }
    default: {
      throw new Error(`Could not plan query for encoding with unknown marks channel: ${encoding.marks.channel}`);
    }
  }
}

export interface PlanSummary<R extends DataRole> {
  readonly type: PlanType.PivotBasic | PlanType.PivotDetail;
  readonly queries: readonly PlanSummaryQuery<R>[];
}

export interface PlanSummaryQuery<R extends DataRole> {
  readonly series: string | '*';
  readonly query: PivotQuery.Basic<R>;
}

export function summarizePivotPlan<R extends DataRole>(plan: PivotPlan<R>): PlanSummary<R> {
  const queries: PlanSummaryQuery<R>[] = [];

  const add = (series: string, query: Query<R>): void => {
    if (query.type === Query.Type.Pivot) {
      queries.push({
        series,
        query: PivotQuery.Basic.make({
          name: query.name,
          sorts: query.sorts,
          filters: query.filters,
          detail: Query.Detail.get(query),
          limit: query.limit,
          offset: query.offset,
          column: query.column,
          x: query.x,
          row: query.row,
          y: query.y,
          names: query.names,
          values: query.values,
          marks: {
            // The channel doesn't matter
            detail: query.marks ?? [],
          },
        }),
      });
    }
  };

  switch (plan.type) {
    case PlanType.PivotBasic: {
      plan.queries.forEach((query) => add('*', query));
      break;
    }
    case PlanType.PivotDetail: {
      for (const level of plan.levels) {
        const query = SelectPlan.Queries.getByName(plan, level.query);
        if (query === undefined) {
          throw new Error(`Could not find query for detail plan level: Series<${level.name}> -> Query<${level.query}>`);
        }
        add(level.name, query);
      }
      break;
    }
  }

  return {
    type: plan.type,
    queries,
  };
}

function makeDetailPlan<R extends ModeRole>(model: Model<R>, viewName: string): PivotDetail<R> {
  return {
    type: PlanType.PivotDetail,
    id: Ident.next('PD'),
    model,
    viewName,
    levels: [],
    parts: [],
    queries: [],
  };
}

function addDetailPlan<R extends ModeRole>(pivot: PivotDetail<R>, query: PivotQuery.Detail<R>): PivotDetail<R> {
  return PivotDetail.extend(pivot, PivotDetail.make(pivot.model, pivot.viewName, query));
}
